import { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import type { Club, ClubEvent, Notice } from "../types";

export function useClub(clubId: string | undefined) {
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const snap = await getDoc(doc(db, "clubs", clubId));
      if (cancelled) return;
      setClub(snap.exists() ? ({ id: snap.id, ...(snap.data() as Omit<Club, "id">) }) : null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  return { club, loading };
}

export function useNotices(clubId: string | undefined) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const q = query(collection(db, "clubs", clubId, "course_info"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      if (cancelled) return;
      const list = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<Notice, "id">) }))
        .filter((n) => n.active);
      setNotices(list);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  return { notices, loading };
}

export function useEvents(clubId: string | undefined) {
  const [events, setEvents] = useState<ClubEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const q = query(collection(db, "clubs", clubId, "events"), orderBy("date", "asc"));
      const snap = await getDocs(q);
      if (cancelled) return;
      const list = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<ClubEvent, "id">) }))
        .filter((e) => e.active);
      setEvents(list);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  return { events, loading };
}
